import { getCookie } from '@atlassian/browser-storage-controls/controllers/cookie-controls/get-cookie';

import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from './constants';

/**
 * Returns `true` when `url` is a same-domain request — either a relative URL
 * (no protocol) or an absolute URL whose origin matches `window.location.origin`.
 *
 * Same-domain detection is used to decide whether to attach the CSRF token
 * header. Tokens are **never** sent cross-domain to prevent credential leakage.
 */
export const isSameDomain = (url: string): boolean =>
	/^(?!https?:)/.test(url) || url.indexOf(window.location.origin) === 0;

/**
 * Build the request headers required for an authenticated same-origin REST call.
 *
 * For same-domain requests the CSRF token (read from the `csrftoken` cookie) is
 * included as `X-CSRFToken`. Cross-domain requests receive no CSRF header.
 *
 * @param url       - The request URL (used for the same-domain check).
 * @param csrftoken - Optional override; defaults to reading the `csrftoken` cookie.
 * @returns A plain headers object suitable for spreading into `RequestInit.headers`.
 */
export const getRequestHeaders = (
	url: string,
	csrftoken: string = getCookie(CSRF_COOKIE_NAME) || '',
): HeadersInit => {
	const headers: HeadersInit = {};

	// Never send tokens cross-(sub)domain
	if (isSameDomain(url)) {
		headers[CSRF_HEADER_NAME] = csrftoken;
	}

	return headers;
};

/**
 * Create a `Request` object pre-configured for authenticated same-origin REST
 * calls.
 *
 * Behaviour:
 * - Sets `credentials: 'same-origin'` so the browser sends cookies with the
 *   request (required for session-based auth).
 * - Attaches the `X-CSRFToken` header for same-domain requests, reading the
 *   token value from the `csrftoken` cookie.
 * - Caller-supplied `options.headers` are merged on top and take precedence
 *   (e.g. `Content-Type` for JSON bodies).
 *
 * @example
 * ```ts
 * const response = await fetch(authRequest('/api/data'));
 * ```
 *
 * @example
 * ```ts
 * const response = await fetch(
 *   authRequest('/api/data', {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify(payload),
 *   }),
 * );
 * ```
 */
export default function authRequest(
	url: string,
	options: RequestInit = {},
	csrftoken: string = getCookie(CSRF_COOKIE_NAME) || '',
): Request {
	const headers = getRequestHeaders(url, csrftoken);

	return new Request(url, {
		credentials: 'same-origin',
		...options,
		headers: {
			...headers,
			...(options.headers || {}),
		},
	});
}
